import { useCallback, useEffect, useMemo, useState } from 'react'
import { FileText, Users, Wand2 } from 'lucide-react'
import { Button, Card, EmptyState, SearchInput } from '@/components/ui'
import { AttributionNotice } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { usePlayer } from '@/features/player'
import { TranscriptLine } from './components/TranscriptLine'
import { findActiveSegmentId } from './activeSegment'
import type { CleanTranscript, MeetingDetail } from '@/types'

/**
 * Transcript + in-transcript search.
 *
 * IT FOLLOWS THE PLAYER WHEN THERE IS ONE, and works without one. `usePlayer`
 * returns null wherever no `MeetingPlayer` is mounted, and there the playhead
 * falls back to local state: clicking a timecode still marks the active line,
 * it just does not move any audio. Nullable rather than required, because a
 * hook that threw would make the transcript unrenderable outside the meeting
 * page — a far worse failure than a timecode that only highlights.
 *
 * The hook this replaces was the opposite mistake. It survived the REMOVAL of
 * the player it existed for and kept building a real `<audio>` element with
 * `preload='metadata'`, so opening this tab requested the whole recording to
 * drive a clock nothing played. The rule that came out of that: the element
 * lives with the thing that plays it, and this panel only ever reads a clock
 * somebody else owns.
 *
 * The segment list is plain DOM. Real meetings reach a few thousand segments,
 * at which point this needs windowing (react-virtual) — the pane is already the
 * scroll container, so that is a swap inside this component and nothing else.
 *
 * ONE COMPONENT SERVES BOTH TABS, via `variant`. The cleaned transcript differs
 * from the recorded one in the text of ~1% of its lines and in nothing else —
 * same segments, same timings, same speakers, same search. A second component
 * would be a copy that drifts, which is exactly what two copies of the
 * action-item serialiser cost once already: the two tabs would start disagreeing
 * about a segment that has nothing to do with repair.
 */
export function TranscriptPanel({
  meeting,
  variant = 'raw',
  onClean,
  isCleaning = false,
  initialSeconds,
}: {
  meeting: MeetingDetail
  variant?: 'raw' | 'clean'
  /**
   * Open with the line at this moment highlighted and scrolled into view -- how
   * a Summary or Actions timecode lands here when the recording cannot play.
   */
  initialSeconds?: number
  /** TEMPORARY: run ASR repair over this meeting now. Ignored on the raw tab. */
  onClean?: () => void
  isCleaning?: boolean
}) {
  const t = useT()
  const clean = variant === 'clean'

  const player = usePlayer()
  // Is there a clock somebody else owns? `isReady` and not merely "a player is
  // mounted": a player that is still loading, or whose meeting has no stored
  // audio, has a `currentTime` frozen at 0 and cannot be seeked.
  //
  // BOTH LINES BELOW MUST KEY ON THE SAME CONDITION. They did not: the
  // playhead read `player?.currentTime ?? localTime` (null-check) while the
  // click handler chose on `isReady`. In the gap between them -- a mounted
  // player that is not ready, which is EVERY recorded meeting whose audio is
  // 404, 503 or still in flight -- the click wrote `localTime` and the
  // playhead went on reading the player's frozen 0, so no line ever
  // highlighted. That is a straight regression of the behaviour that shipped,
  // and in mock mode it is the behaviour of the whole app.
  const following = player?.isReady ? player : null

  // Where the reader is in the transcript. Driven by the player when one can
  // play, otherwise moved only by clicking a timecode.
  const [localTime, setLocalTime] = useState(initialSeconds ?? 0)
  const currentTime = following?.currentTime ?? localTime
  const [query, setQuery] = useState('')

  // Clicking a timecode PLAYS FROM THERE rather than only seeking. On this
  // page the timecode has always been labelled "play from", and a control
  // that moves a paused playhead invisibly is indistinguishable from one that
  // did nothing. With no playable audio it is the old behaviour: mark the
  // line.
  //
  // AN EXPIRED URL IS RETRIED FROM HERE TOO. In `media-error` the click used to
  // mark the line and nothing else, so the audio stayed dead until somebody
  // found the bar's Try again -- which then resumed at the old playhead. The
  // line is marked first, so a failed refresh still leaves the reader on it.
  // Stable (every dep is), because each memoised line receives it.
  const playFrom = following?.playFrom
  const retry = player?.state === 'media-error' ? player.retry : undefined
  const onSeek = useCallback(
    (seconds: number) => {
      if (playFrom) return playFrom(seconds)
      setLocalTime(seconds)
      if (retry) void retry(seconds)
    },
    [playFrom, retry],
  )

  const activeSegmentId = useMemo(
    () => findActiveSegmentId(meeting.segments, currentTime),
    [meeting.segments, currentTime],
  )

  // FOLLOW THE PLAYHEAD, BUT ONLY WHILE PLAYING. Scrolling on every change of
  // active line would fight somebody reading ahead while paused, and `nearest`
  // rather than `center` means a line already on screen does not move at all
  // — so the list only jumps when the conversation has actually left the
  // viewport.
  useEffect(() => {
    if (!following?.isPlaying || activeSegmentId === null) return
    document
      .getElementById(`segment-${activeSegmentId}`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [activeSegmentId, following?.isPlaying])

  // Once, on arrival from a timecode elsewhere. Centred rather than `nearest`:
  // the reader came here for this line, so it should not sit at an edge.
  useEffect(() => {
    if (initialSeconds === undefined || activeSegmentId === null) return
    document
      .getElementById(`segment-${activeSegmentId}`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return new Set<string>()
    // Search the text the reader can actually SEE. Searching `text` in the
    // cleaned tab would report matches on words that are not on screen, and
    // miss the repaired spelling — which is the one people will search for.
    return new Set(
      meeting.segments
        .filter((segment) =>
          (clean ? (segment.textClean ?? segment.text) : segment.text)
            .toLowerCase()
            .includes(term),
        )
        .map((s) => s.id),
    )
  }, [meeting.segments, query, clean])

  if (clean && meeting.cleanTranscript === null) {
    return (
      <Card>
        <EmptyState
          icon={<Wand2 />}
          title={t('transcript.noCleanedTitle')}
          description={
            meeting.segments.length === 0
              ? t('transcript.noCleanedNoTranscript')
              : t('transcript.noCleanedNotRun')
          }
          action={
            onClean && meeting.segments.length > 0 ? (
              <Button
                variant="primary"
                onClick={onClean}
                isLoading={isCleaning}
                leftIcon={<Wand2 aria-hidden className="h-4 w-4" />}
              >
                {t('transcript.cleanThisTranscript')}
              </Button>
            ) : undefined
          }
        />
      </Card>
    )
  }

  if (meeting.segments.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileText />}
          title={meeting.status === 'processing' ? t('transcript.inProgressTitle') : t('transcript.noTranscriptTitle')}
          description={
            meeting.status === 'processing'
              ? t('transcript.inProgressBody')
              : t('transcript.noTranscriptBody')
          }
        />
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <AttributionNotice check={meeting.clockCheck} />
      {clean && meeting.cleanTranscript && (
        <CleanTranscriptNotice
          info={meeting.cleanTranscript}
          onClean={onClean}
          isCleaning={isCleaning}
        />
      )}

      <Card className="flex max-h-[calc(100dvh-14rem)] flex-col">
        <div className="flex flex-col gap-2 border-b border-border px-3 py-2 sm:flex-row sm:items-center">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('transcript.find')}
            className="sm:max-w-xs"
            aria-label={t('transcript.searchAria')}
          />
          {query && (
            <span className="text-sm text-fg-muted sm:ml-auto" aria-live="polite">
              {matches.size === 1
                ? t('transcript.match', { count: matches.size })
                : t('transcript.matches', { count: matches.size })}
            </span>
          )}
        </div>

        <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
          {meeting.segments.map((segment) => (
            <TranscriptLine
              key={segment.id}
              segment={segment}
              isActive={segment.id === activeSegmentId}
              isMatch={matches.has(segment.id)}
              onSeek={onSeek}
              clean={clean}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 border-t border-border px-3 py-2 text-sm text-fg-muted">
          <Users aria-hidden className="h-3.5 w-3.5" />
          {t('transcript.participantsCount', { count: meeting.participants.length })} ·{' '}
          {t('transcript.distinctVoices', {
            count: new Set(meeting.segments.map((s) => s.speakerName ?? s.speakerLabel)).size,
          })}
        </div>
      </Card>
    </div>
  )
}

/**
 * What the repair did, and what it deliberately did not touch.
 *
 * The second sentence is the one that has to be there. The summary, its
 * decisions and its action items are generated from the RECORDED transcript,
 * not this one — so a reader who spots a repaired line here must not conclude
 * the summary was built on it. Saying so in the product is cheaper than
 * answering it once per customer.
 */
function CleanTranscriptNotice({
  info,
  onClean,
  isCleaning = false,
}: {
  info: CleanTranscript
  onClean?: () => void
  isCleaning?: boolean
}) {
  const t = useT()
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
      <Wand2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
      <p className="min-w-0 flex-1">
        {info.edits === 0 ? (
          <>
            {t('transcript.notice.changedNothingPrefix')}
            <strong className="font-medium text-fg">{t('transcript.notice.nothing')}</strong>.
          </>
        ) : (
          <>
            <strong className="font-medium text-fg">
              {info.edits === 1
                ? t('transcript.notice.lineRepaired', { count: info.edits })
                : t('transcript.notice.linesRepaired', { count: info.edits })}
            </strong>
            {info.model
              ? t('transcript.notice.repairedBy', { model: info.model })
              : t('transcript.notice.repairedPlain')}
          </>
        )}
        {t('transcript.notice.summaryNote')}
      </p>
      {onClean && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClean}
          isLoading={isCleaning}
          className="shrink-0"
        >
          {t('transcript.notice.runAgain')}
        </Button>
      )}
    </div>
  )
}
